import { onScopeDispose, ref } from 'vue';

/**
 * Detects a new deployment for a long-open SPA tab and auto-refreshes safely.
 *
 * Behaviour:
 * - Captures the *loaded* build id once (injected via `import.meta.env.VITE_BUILD_ID`).
 * - Polls a cache-busted, base-aware `version.json` on an interval, and on tab
 *   refocus / visibility-visible; polling is paused while the tab is hidden.
 * - When the deployed build id differs from the loaded one -> `updateAvailable`.
 * - Failure-safe: any network/parse error or missing/blank id is a no-op - it
 *   never throws, never white-screens, and never false-positives a reload.
 * - Reloads at most once per detected build id (sessionStorage guard) so a
 *   mis-served version.json can never cause a reload loop.
 */

const DEFAULT_POLL_INTERVAL_MS = 90_000; // 90s
const RELOAD_GUARD_KEY = 'version-reload-attempt';

interface VersionResponse {
  buildId: string;
}

/** Narrows an unknown JSON payload to a well-formed `{ buildId: string }`. */
function isVersionResponse(data: unknown): data is VersionResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'buildId' in data &&
    typeof (data as { buildId: unknown }).buildId === 'string'
  );
}

export interface UseVersionCheckOptions {
  /**
   * Returns true when reloading would lose user work (dirty form, upload,
   * editing modal). The safe-reload triggers defer while this is true.
   * Defaults to "never unsafe".
   */
  isUnsafe?: () => boolean;
  /** Poll interval in ms. Defaults to 90s. */
  pollIntervalMs?: number;
}

export function useVersionCheck(options: UseVersionCheckOptions = {}) {
  const { isUnsafe, pollIntervalMs = DEFAULT_POLL_INTERVAL_MS } = options;

  const loadedBuildId: string | null =
    (import.meta.env.VITE_BUILD_ID as string | undefined) ?? null;

  const updateAvailable = ref(false);
  const deployedBuildId = ref<string | null>(null);

  // Reload at most once per build id. The guard is keyed by the *target*
  // build id, so if a reload doesn't actually change the loaded id
  // (mis-served version.json) we detect the same id again and skip - no loop.
  function doReload(targetId: string) {
    try {
      if (sessionStorage.getItem(RELOAD_GUARD_KEY) === targetId) return;
      sessionStorage.setItem(RELOAD_GUARD_KEY, targetId);
    } catch {
      // sessionStorage unavailable (private mode / disabled) - reload once.
    }
    window.location.reload();
  }

  function reloadNow() {
    const target = deployedBuildId.value;
    if (target) doReload(target);
  }

  function reloadIfSafe() {
    const target = deployedBuildId.value;
    if (!target) return;
    if (isUnsafe?.()) return;
    doReload(target);
  }

  // Fetch version.json and compare. Returns true if an update is available.
  // Always resolves - never rejects - so callers can chain safely.
  async function check(): Promise<boolean> {
    if (!loadedBuildId) return false;
    try {
      const res = await fetch(
        `${import.meta.env.BASE_URL}version.json?t=${Date.now()}`,
        { cache: 'no-store' }
      );
      if (!res.ok) return false;
      const data: unknown = await res.json();
      const id = isVersionResponse(data) ? data.buildId : null;
      if (!id || id === loadedBuildId) return false;
      deployedBuildId.value = id;
      updateAvailable.value = true;
      return true;
    } catch {
      // Network error / unparseable / offline - stay silent.
      return false;
    }
  }

  let intervalId: ReturnType<typeof setInterval> | undefined;

  function startPolling() {
    if (intervalId !== undefined) return;
    intervalId = setInterval(() => {
      void check();
    }, pollIntervalMs);
  }

  function stopPolling() {
    if (intervalId !== undefined) {
      clearInterval(intervalId);
      intervalId = undefined;
    }
  }

  // Returning to a stale tab is exactly the case this feature targets: check,
  // and if a new build is live, refresh at this safe moment (unless unsafe).
  function checkAndMaybeReload() {
    void check().then((available) => {
      if (available) reloadIfSafe();
    });
  }

  function onVisibility() {
    if (document.hidden) {
      stopPolling();
    } else {
      startPolling();
      checkAndMaybeReload();
    }
  }

  function onFocus() {
    checkAndMaybeReload();
  }

  // No loaded build id (e.g. inject missing) -> feature is fully inert.
  if (loadedBuildId) {
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    // Kick off immediately if the tab is currently visible.
    if (!document.hidden) {
      startPolling();
      void check();
    }

    onScopeDispose(() => {
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    });
  }

  return {
    updateAvailable,
    loadedBuildId,
    deployedBuildId,
    reloadNow,
    reloadIfSafe,
  };
}
